/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useForumType } from "@/components/hooks/useForumType";
import React, { type JSX } from 'react';
import classNames from 'classnames';

import {
  $isAutoLinkNode,
  $isLinkNode,
  LinkNode,
  TOGGLE_LINK_COMMAND,
} from '@lexical/link';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {$generateHtmlFromNodes, $generateNodesFromDOM} from '@lexical/html';
import {
  $createParagraphNode,
  $createRangeSelection,
  $getEditor,
  $getNodeByKey,
  $getSelection,
  $insertNodes,
  $isParagraphNode,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  LexicalNode,
  LexicalCommand,
  LexicalEditor,
  NodeKey,
  PASTE_COMMAND,
  ParagraphNode,
  TextNode,
} from 'lexical';
import {useEffect, useId, useRef, useState} from 'react';


import {
  $createImageNode,
  $createImageCaptionNode,
  $createImageRenderNode,
  $isImageCaptionNode,
  $isImageNode,
  $isImageRenderNode,
  ImageCaptionNode,
  ImageNode,
  ImagePayload,
} from '../../nodes/ImageNode';
import { $canDropImage, $getImageNodeInSelection, getDragImageData, getDragSelection, isImageFile } from './ImageUtils';
import { preloadImage } from '../../nodes/imageCache';

import {
  SET_IMAGE_CAPTION_VISIBILITY_COMMAND,
  SET_IMAGE_SIZE_COMMAND,
  type SetImageCaptionVisibilityPayload,
  type SetImageSizePayload,
} from './commands';
import {
  uploadToCloudinary,
  ImageUploadError,
} from '../../utils/cloudinaryUpload';
import { INSERT_FILE_COMMAND } from '@/components/editor/lexicalPlugins/suggestions/Events'
import { useMessages } from '@/components/common/withMessages'
import { WithMessagesMessage } from '@/components/layout/FlashMessages';
import LWDialog from '@/components/common/LWDialog';
import { DialogTitle } from '@/components/widgets/DialogTitle';
import { DialogContent } from '@/components/widgets/DialogContent';
import { DialogActions } from '@/components/widgets/DialogActions';
import Button from '@/lib/vendor/@material-ui/core/src/Button';
import TextField from '@/lib/vendor/@material-ui/core/src/TextField';
import { defineStyles, useStyles } from '@/components/hooks/useStyles';
import { FileImageIcon } from '../../icons/FileImageIcon';

const imageDialogStyles = defineStyles('InsertImageDialog', (theme: ThemeType) => ({
  paper: {
    width: 480,
    maxWidth: 'calc(100vw - 32px)',
    borderRadius: 12,
    fontFamily: theme.palette.fonts.sansSerifStack,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 600,
    color: theme.palette.grey[900],
  },
  dropZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: 24,
    border: `2px dashed ${theme.palette.greyAlpha(0.2)}`,
    borderRadius: 8,
    background: theme.palette.greyAlpha(0.025),
    textAlign: 'center',
    transition: 'border-color 150ms, background 150ms',
  },
  dragging: {
    borderColor: theme.palette.primary.main,
    background: theme.palette.greyAlpha(0.07),
  },
  imageIcon: {
    width: 32,
    height: 32,
    color: theme.palette.grey[500],
  },
  dropLabel: {
    fontSize: 15,
    fontWeight: 500,
    color: theme.palette.grey[800],
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  hint: {
    fontSize: 13,
    color: theme.palette.grey[600],
  },
  preview: {
    display: 'block',
    maxWidth: '100%',
    height: 120,
    objectFit: 'contain',
    borderRadius: 4,
  },
  fileInput: {
    display: 'none',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '20px 0 8px',
    fontSize: 13,
    color: theme.palette.grey[600],
    '&::before, &::after': {
      content: '""',
      flex: 1,
      borderTop: theme.palette.greyBorder('1px', 0.12),
    },
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: 12,
    fontSize: 14,
  },
  actions: {
    margin: '24px 0 0',
  },
}));

export type InsertImagePayload = Readonly<ImagePayload>;

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> = createCommand('INSERT_IMAGE_COMMAND');

function findCaptionAncestor(node: LexicalNode | null): ImageCaptionNode | null {
  let current: LexicalNode | null = node;
  while (current) {
    if ($isImageCaptionNode(current)) {
      return current;
    }
    current = current.getParent();
  }
  return null;
}

function updateCaptionEmptyAttribute(
  editor: LexicalEditor,
  captionNode: ImageCaptionNode,
): void {
  const element = editor.getElementByKey(captionNode.getKey());
  if (element) {
    element.setAttribute('data-empty', captionNode.isEmpty() ? 'true' : 'false');
  }
}

function updateCaptionEmptyFromMutations(
  editor: LexicalEditor,
  mutations: Map<NodeKey, 'created' | 'updated' | 'destroyed'>,
): void {
  editor.getEditorState().read(() => {
    for (const [nodeKey, mutation] of mutations) {
      if (mutation === 'destroyed') {
        continue;
      }
      const node = $getNodeByKey(nodeKey);
      if (node) {
        const captionNode = findCaptionAncestor(node);
        if (captionNode) {
          updateCaptionEmptyAttribute(editor, captionNode);
        }
      }
    }
  });
}

export function InsertImageDialog({
  activeEditor,
  onClose,
  onError,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
  onError?: (error: Error) => void;
}): JSX.Element {
  const { forumType } = useForumType();
  const classes = useStyles(imageDialogStyles);
  const titleId = useId();
  const urlId = useId();
  const altTextId = useId();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [src, setSrc] = useState('');
  const [altText, setAltText] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const handleFileSelect = (files: FileList | null) => {
    if (abortControllerRef.current || !files?.length) return;
    if (files.length !== 1) {
      setError('Please choose one image at a time.');
      return;
    }
    const file = files[0];
    if (!isImageFile(file)) {
      setError('Please choose an image file.');
      return;
    }
    setError(null);
    setSelectedFile(file);
    setSrc('');
  };

  const handleDragEnter = (event: React.DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    if (!isUploading && event.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (event: React.DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = isUploading ? 'none' : 'copy';
  };

  const handleDrop = (event: React.DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);
    handleFileSelect(event.dataTransfer.files);
  };

  const handleInsert = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (abortControllerRef.current || (!selectedFile && !src.trim())) return;
    setError(null);

    if (!selectedFile) {
      let url: URL;
      try {
        url = new URL(src.trim());
      } catch {
        setError('Enter a valid image URL starting with https:// or http://.');
        return;
      }
      if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        setError('Enter a valid image URL starting with https:// or http://.');
        return;
      }
      activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, { src: src.trim(), altText });
      onClose();
      return;
    }

    setIsUploading(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const result = await uploadToCloudinary(selectedFile, forumType, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, {
        altText,
        src: result.secure_url,
        width: result.width,
        height: result.height,
      });
      onClose();
    } catch (uploadError) {
      if (controller.signal.aborted) return;
      setError(uploadError instanceof ImageUploadError && uploadError.isUserFacing
        ? uploadError.message
        : 'Failed to upload image. Please try again.');
      if (uploadError instanceof Error) onError?.(uploadError);
    } finally {
      if (!controller.signal.aborted) setIsUploading(false);
      abortControllerRef.current = null;
    }
  };

  return (
    <LWDialog open={true} onClose={onClose} maxWidth={false} paperClassName={classes.paper}>
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={isUploading}
        onSubmit={handleInsert}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <DialogTitle disableTypography>
          <h2 id={titleId} className={classes.title}>Insert image</h2>
        </DialogTitle>
        <DialogContent>
          <div className={classNames(classes.dropZone, { [classes.dragging]: isDragging })}>
            {previewUrl
              ? <img src={previewUrl} alt="Selected image preview" className={classes.preview} />
              : <FileImageIcon className={classes.imageIcon} viewBox="0 0 16 16" aria-hidden="true" />}
            <div className={classes.dropLabel}>
              {isDragging ? 'Drop your image here' : selectedFile?.name ?? 'Drag an image here'}
            </div>
            <Button
              type="button"
              variant="outlined"
              autoFocus
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
              data-test-id="image-modal-option-file"
            >
              {selectedFile ? 'Choose another file' : 'Choose a file'}
            </Button>
            <input
              ref={fileInputRef}
              className={classes.fileInput}
              type="file"
              accept="image/*"
              disabled={isUploading}
              aria-label="Choose an image file"
              onChange={(event) => {
                handleFileSelect(event.target.files);
                event.target.value = '';
              }}
              data-test-id="image-modal-file-upload"
            />
          </div>
          <div className={classes.divider}>or use a link</div>
          <TextField
            id={urlId}
            label="Image URL"
            placeholder="https://example.com/image.jpg"
            value={src}
            disabled={isUploading}
            onChange={(event) => {
              setSrc(event.target.value);
              setSelectedFile(null);
              setError(null);
            }}
            fullWidth
            margin="dense"
            data-test-id="image-modal-url-input"
          />
          <TextField
            id={altTextId}
            label="Alt text (optional)"
            placeholder="Describe the image"
            helperText="A description for people using screen readers."
            value={altText}
            disabled={isUploading}
            onChange={(event) => setAltText(event.target.value)}
            fullWidth
            margin="dense"
            data-test-id="image-modal-alt-text-input"
          />
          {error && <div className={classes.errorText} role="alert">{error}</div>}
          <DialogActions className={classes.actions}>
            <Button type="button" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isUploading || (!selectedFile && !src.trim())}
              data-test-id="image-modal-confirm-btn"
            >
              {isUploading ? 'Uploading…' : 'Insert image'}
            </Button>
          </DialogActions>
        </DialogContent>
      </form>
    </LWDialog>
  );
}

function flashUploadError(
  flash: (message: WithMessagesMessage) => void,
  error: unknown,
): void {
  const errorMessage =
    error instanceof ImageUploadError && error.isUserFacing
      ? error.message
      : 'Failed to upload image. Please try again.';
  flash({ messageString: errorMessage, type: 'error' });
}

export default function ImagesPlugin({
  captionsEnabled,
}: {
  captionsEnabled?: boolean;
}): JSX.Element | null {
  const { forumType } = useForumType();
  const [editor] = useLexicalComposerContext();
  const { flash } = useMessages();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand<SetImageSizePayload>(
        SET_IMAGE_SIZE_COMMAND,
        ({ nodeKey, widthPercent }) => {
          const node = $getNodeByKey(nodeKey);
          if (!$isImageNode(node)) {
            return false;
          }
          node.setWidthPercent(widthPercent);
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand<SetImageCaptionVisibilityPayload>(
        SET_IMAGE_CAPTION_VISIBILITY_COMMAND,
        ({ nodeKey, showCaption }) => {
          const node = $getNodeByKey(nodeKey);
          if (!$isImageNode(node)) {
            return false;
          }
          node.setShowCaption(showCaption);
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand<File | Blob>(
        INSERT_FILE_COMMAND,
        (payload) => {
          if (!isImageFile(payload) || !(payload instanceof File)) {
            return false;
          }
          uploadToCloudinary(payload, forumType)
            .then((result) => {
              editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                altText: payload.name,
                src: result.secure_url,
                width: result.width,
                height: result.height,
              });
            })
            .catch((error) => {
              flashUploadError(flash, error);
            });
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          const clipboardData = 'clipboardData' in event ? event.clipboardData : null;
          if (!clipboardData) {
            return false;
          }
          const files = Array.from(clipboardData.files);
          const imageFiles = files.filter(isImageFile);
          if (imageFiles.length === 0) {
            return false;
          }
          event.preventDefault();
          for (const file of imageFiles) {
            uploadToCloudinary(file, forumType)
              .then((result) => {
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  altText: file.name || 'Pasted image',
                  src: result.secure_url,
                  width: result.width,
                  height: result.height,
                });
              })
              .catch((error) => {
                flashUploadError(flash, error);
              });
          }
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),
      editor.registerCommand<InsertImagePayload>(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode(payload);
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }
          const parent = imageNode.getParent();
          if (
            parent &&
            $isRootOrShadowRoot(parent) &&
            imageNode.getNextSibling() === null
          ) {
            const trailingParagraph = $createParagraphNode();
            imageNode.insertAfter(trailingParagraph);
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand<DragEvent>(
        DRAGSTART_COMMAND,
        (event) => {
          return $onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand<DragEvent>(
        DRAGOVER_COMMAND,
        (event) => {
          return $onDragover(event);
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          return $onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerUpdateListener(({dirtyElements, tags}) => {
        if (tags.has('collaboration')) {
          return;
        }
        editor.update(() => {
          for (const key of dirtyElements.keys()) {
            const node = $getNodeByKey(key);
            if ($isImageNode(node)) {
              enforceImageNodeStructure(node);
            }
          }
        });
      }),
      editor.registerMutationListener(ImageCaptionNode, (mutations) => {
        editor.getEditorState().read(() => {
          for (const [nodeKey] of mutations) {
            const captionNode = $getNodeByKey(nodeKey);
            if ($isImageCaptionNode(captionNode)) {
              updateCaptionEmptyAttribute(editor, captionNode);
            }
          }
        });
      }),
      editor.registerMutationListener(TextNode, (mutations) => {
        updateCaptionEmptyFromMutations(editor, mutations);
      }),
      editor.registerMutationListener(ParagraphNode, (mutations) => {
        updateCaptionEmptyFromMutations(editor, mutations);
      }),
      editor.registerMutationListener(ImageNode, (mutations) => {
        const nodeKeysToRedecorate: string[] = [];

        for (const [nodeKey, mutation] of mutations) {
          if (mutation === 'destroyed') continue;

          // Ensure the render node re-decorates on any ImageNode change.
          // This is necessary because collaboration updates modify node
          // properties directly without going through setter methods like
          // setSrc(), so the render node isn't automatically marked dirty.
          nodeKeysToRedecorate.push(nodeKey);

          if (mutation !== 'created') continue;

          editor.getEditorState().read(() => {
            const node = $getNodeByKey(nodeKey);
            if (!$isImageNode(node)) return;
            const src = node.getSrc();
            if (!src.startsWith('blob:')) return;

            fetch(src)
              .then(response => response.blob())
              .then(blob => uploadToCloudinary(blob, forumType))
              .then(async (result) => {
                await preloadImage(result.secure_url);
                editor.update(() => {
                  const currentNode = $getNodeByKey(nodeKey);
                  if ($isImageNode(currentNode)) {
                    currentNode.setSrc(result.secure_url);
                  }
                });
                URL.revokeObjectURL(src);
              })
              .catch((error) => {
                // On non-creating clients, the blob URL is unresolvable —
                // the creating client will handle the upload and propagate
                // the Cloudinary URL via Yjs.
                if (error instanceof ImageUploadError) {
                  flashUploadError(flash, error);
                }
              });
          });
        }

        if (nodeKeysToRedecorate.length > 0) {
          editor.update(() => {
            for (const nodeKey of nodeKeysToRedecorate) {
              const node = $getNodeByKey(nodeKey);
              if (!$isImageNode(node)) continue;
              const renderNode = node.getFirstChild();
              if ($isImageRenderNode(renderNode)) {
                renderNode.getWritable();
              }
            }
          });
        }
      }),
    );
  }, [captionsEnabled, editor, flash, forumType]);

  return null;
}

/**
 * Normalize image nodes after any update.
 *
 * Ensures the image node has a stable internal structure:
 * - Exactly one render child, always first.
 * - At most one caption child when showCaption is enabled.
 * - Removes any unexpected children.
 * - Unwraps the image from a paragraph when it is the only child.
 * - Ensures a trailing paragraph exists when the image is the last root child.
 */
function enforceImageNodeStructure(node: ImageNode): void {
  let renderNode: ReturnType<typeof $createImageRenderNode> | null = null;
  const captionNodes: Array<ReturnType<typeof $createImageCaptionNode>> = [];
  for (const child of node.getChildren()) {
    if ($isImageRenderNode(child)) {
      if (!renderNode) {
        renderNode = child;
      } else {
        child.remove();
      }
    } else if ($isImageCaptionNode(child)) {
      captionNodes.push(child);
    } else {
      child.remove();
    }
  }

  if (!renderNode) {
    renderNode = $createImageRenderNode();
    const firstChild = node.getFirstChild();
    if (firstChild) {
      firstChild.insertBefore(renderNode);
    } else {
      node.append(renderNode);
    }
  } else if (node.getFirstChild() !== renderNode) {
    renderNode.remove();
    node.splice(0, 0, [renderNode]);
  }

  if (captionNodes.length > 0 && !node.getShowCaption()) {
    node.setShowCaption(true);
  }

  if (node.getShowCaption()) {
    let captionNode = captionNodes[0] ?? null;
    for (let i = 1; i < captionNodes.length; i += 1) {
      captionNodes[i].remove();
    }
    if (!captionNode) {
      captionNode = $createImageCaptionNode();
      node.append(captionNode);
    }
    if (captionNode.getChildrenSize() === 0) {
      captionNode.append($createParagraphNode());
    }
  } else {
    for (const captionNode of captionNodes) {
      captionNode.remove();
    }
  }

  const nodeParent = node.getParent();
  if ($isParagraphNode(nodeParent) && nodeParent.getChildrenSize() === 1) {
    nodeParent.replace(node);
  }

  const parent = node.getParent();
  if (parent && $isRootOrShadowRoot(parent) && node.getNextSibling() === null) {
    node.insertAfter($createParagraphNode());
  }
}

const TRANSPARENT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
let img: HTMLImageElement | null = null;

function $onDragStart(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData('text/plain', '_');
  if (!img) {
    img = document.createElement('img');
    img.src = TRANSPARENT_IMAGE;
  }
  dataTransfer.setDragImage(img, 0, 0);

  const editor = $getEditor();
  const selection = $getSelection();
  const dragHtml =
    selection !== null
      ? editor.getEditorState().read(() =>
          $generateHtmlFromNodes(editor, selection),
        )
      : null;

  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.getAltText(),
        height: node.getHeight(),
        key: node.getKey(),
        maxWidth: node.getMaxWidth(),
        showCaption: node.getShowCaption(),
        src: node.getSrc(),
        srcset: node.getSrcset(),
        width: node.getWidth(),
        widthPercent: node.getWidthPercent(),
        isCkFigure: node.getIsCkFigure(),
        captionsEnabled: node.getCaptionsEnabled(),
        html: dragHtml,
      },
      type: 'image',
    }),
  );

  return true;
}

function $onDragover(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!$canDropImage(event)) {
    event.preventDefault();
  }
  return false;
}

function $onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  const existingLink = $findMatchingParent(
    node,
    (parent): parent is LinkNode =>
      !$isAutoLinkNode(parent) && $isLinkNode(parent),
  );
  event.preventDefault();
  if ($canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    if (data.html && typeof DOMParser !== 'undefined') {
      const dom = new DOMParser().parseFromString(data.html, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      $insertNodes(nodes);
    } else {
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, data);
    }
    if (existingLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, existingLink.getURL());
    }
  }
  return true;
}
